import { useEffect, useState } from "react";
import {
  Alert,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Typography,
  message as antMessage,
} from "antd";
import { useAuth } from "shared/contexts/AuthProvider";
import { orgStoragesService } from "../services/orgStorageService";
import { getErrorDetail } from "./OrgStorageSection";
import { BUTTON_STYLES } from "../constants";

const { Text } = Typography;

const EMPTY_FORM = {
  name: "",
  storage_type: "s3",
  managed: true,
  endpoint: "",
  bucket: "",
  region: "",
  secure: true,
  public_endpoint: "",
  local_endpoint: "",
  access_key: "",
  base_path: "",
  host: "",
  enabled: true,
};

/**
 * Add/edit modal for one organization storage. Fields switch on
 * storage_type × managed; the secret key is write-only (blank = keep the
 * stored secret on edit).
 *
 * @param {boolean} open - Modal visibility
 * @param {string} orgId - Organization ID
 * @param {Object|null} storage - Storage to edit; null = create
 * @param {Function} translate - Translation function
 * @param {Function} onClose - Close without saving
 * @param {Function} onSaved - Called after a successful create/update
 */
export const StorageEditModal = ({
  open,
  orgId,
  storage,
  translate = (key) => key,
  onClose,
  onSaved,
}) => {
  const { authFetch } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [secretKey, setSecretKey] = useState("");
  const [saving, setSaving] = useState(false);

  const isEdit = !!storage;
  const hasSecret = !!storage?.config?.has_secret;

  useEffect(() => {
    if (!open) return;
    setSecretKey("");
    if (storage) {
      const config = storage.config || {};
      setForm({
        name: storage.name || "",
        storage_type: storage.storage_type || "s3",
        managed: !!storage.managed,
        endpoint: config.endpoint || "",
        bucket: config.bucket || "",
        region: config.region || "",
        secure: config.secure !== undefined ? config.secure : true,
        public_endpoint: config.public_endpoint || "",
        local_endpoint: config.local_endpoint || "",
        access_key: config.access_key || "",
        base_path: config.base_path || "",
        host: config.host || "",
        enabled: storage.enabled !== undefined ? storage.enabled : true,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, storage]);

  const setField = (updates) => setForm((prev) => ({ ...prev, ...updates }));

  const buildConfig = () => {
    if (form.storage_type === "filesystem") {
      return {
        storage_type: "filesystem",
        base_path: form.base_path.trim(),
        host: form.host.trim() || null,
      };
    }
    const config = {
      storage_type: "s3",
      endpoint: form.endpoint.trim(),
      bucket: form.bucket.trim(),
      region: form.region.trim() || null,
      secure: form.secure,
      public_endpoint: form.public_endpoint.trim() || null,
      local_endpoint: form.local_endpoint.trim() || null,
    };
    if (form.managed) {
      config.access_key = form.access_key.trim() || null;
      // secret_key is write-only: blank means "keep the stored secret",
      // so it must be OMITTED from the payload
      if (secretKey) {
        config.secret_key = secretKey;
      }
    }
    return config;
  };

  const handleSave = async () => {
    const isFilesystem = form.storage_type === "filesystem";
    const payload = {
      name: form.name.trim(),
      managed: isFilesystem ? false : form.managed,
      config: buildConfig(),
      enabled: form.enabled,
    };
    try {
      setSaving(true);
      if (isEdit) {
        await orgStoragesService.update(
          orgId,
          storage.storage_id,
          payload,
          authFetch,
        );
      } else {
        payload.storage_type = form.storage_type;
        await orgStoragesService.create(orgId, payload, authFetch);
      }
      antMessage.success(translate("organizations.storage.saveSuccess"));
      onSaved?.();
    } catch (error) {
      antMessage.error(
        translate("organizations.storage.saveFailed") + getErrorDetail(error),
      );
    } finally {
      setSaving(false);
    }
  };

  const isFilesystem = form.storage_type === "filesystem";

  return (
    <Modal
      open={open}
      title={
        isEdit
          ? translate("organizations.storages.editTitle")
          : translate("organizations.storages.addTitle")
      }
      okText={translate("organizations.storage.save")}
      confirmLoading={saving}
      onOk={handleSave}
      onCancel={onClose}
      destroyOnHide
      okButtonProps={
        {style: 
          { ...BUTTON_STYLES.add,
            display: "inline-block",
          }}}
    >
      <Form layout="vertical">
        <Form.Item label={translate("organizations.storages.name")} required>
          <Input
            value={form.name}
            onChange={(e) => setField({ name: e.target.value })}
            placeholder="archive-s3"
          />
        </Form.Item>

        <Form.Item label={translate("organizations.storages.type")}>
          <Select
            value={form.storage_type}
            disabled={isEdit}
            onChange={(value) =>
              setField({
                storage_type: value,
                managed: value === "filesystem" ? false : form.managed,
              })
            }
            options={[
              { value: "s3", label: "S3 / MinIO" },
              {
                value: "filesystem",
                label: translate("organizations.storages.typeFilesystem"),
              },
            ]}
          />
        </Form.Item>

        {!isFilesystem && (
          <Form.Item
            label={translate("organizations.storages.managedLabel")}
            extra={translate("organizations.storages.managedHint")}
          >
            <Switch
              checked={form.managed}
              onChange={(checked) => setField({ managed: checked })}
            />
          </Form.Item>
        )}

        {isFilesystem ? (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={translate("organizations.storages.filesystemInfo")}
            />
            <Form.Item
              label={translate("organizations.storages.basePath")}
              required
            >
              <Input
                value={form.base_path}
                onChange={(e) => setField({ base_path: e.target.value })}
                placeholder="/srv/mcap-logs"
              />
            </Form.Item>
            <Form.Item label={translate("organizations.storages.host")}>
              <Input
                value={form.host}
                onChange={(e) => setField({ host: e.target.value })}
                placeholder="storage-server-01"
              />
            </Form.Item>
          </>
        ) : (
          <>
            {!form.managed && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={translate("organizations.storage.privateInfoTitle")}
                description={translate("organizations.storage.privateInfo")}
              />
            )}
            <Form.Item
              label={translate("organizations.storage.endpoint")}
              required
            >
              <Input
                value={form.endpoint}
                onChange={(e) => setField({ endpoint: e.target.value })}
                placeholder="minio.example.com:9000"
              />
            </Form.Item>
            <Form.Item
              label={translate("organizations.storage.bucket")}
              required
            >
              <Input
                value={form.bucket}
                onChange={(e) => setField({ bucket: e.target.value })}
                placeholder="mcap-logs"
              />
            </Form.Item>
            <Form.Item label={translate("organizations.storage.region")}>
              <Input
                value={form.region}
                onChange={(e) => setField({ region: e.target.value })}
                placeholder="us-east-1"
              />
            </Form.Item>
            {form.managed && (
              <>
                <Form.Item
                  label={translate("organizations.storage.accessKey")}
                  required
                >
                  <Input.Password
                    value={form.access_key}
                    onChange={(e) => setField({ access_key: e.target.value })}
                    autoComplete="new-password"
                  />
                </Form.Item>
                <Form.Item
                  label={translate("organizations.storage.secretKey")}
                  extra={
                    hasSecret
                      ? translate("organizations.storage.secretKeepHint")
                      : undefined
                  }
                >
                  <Input.Password
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder={
                      hasSecret
                        ? translate("organizations.storage.secretUnchanged")
                        : ""
                    }
                    autoComplete="new-password"
                  />
                </Form.Item>
                <Form.Item
                  label={translate("organizations.storage.publicEndpoint")}
                  extra={translate("organizations.storage.publicEndpointHint")}
                >
                  <Input
                    value={form.public_endpoint}
                    onChange={(e) =>
                      setField({ public_endpoint: e.target.value })
                    }
                    placeholder="storage.example.org"
                  />
                </Form.Item>
              </>
            )}
          </>
        )}

        <Space size="large">
          {!isFilesystem && (
            <Space>
              <Switch
                checked={form.secure}
                onChange={(checked) => setField({ secure: checked })}
              />
              <Text>{translate("organizations.storage.secure")}</Text>
            </Space>
          )}
          <Space>
            <Switch
              checked={form.enabled}
              onChange={(checked) => setField({ enabled: checked })}
            />
            <Text>{translate("organizations.storage.enabled")}</Text>
          </Space>
        </Space>
      </Form>
    </Modal>
  );
};
